import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiService from "../../services/api";
import { BulkEmailRecipient } from "../../types";

interface RecipientsState {
  recipients: BulkEmailRecipient[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  totalPages: number;
  selectedRecipients: string[];
}

const initialState: RecipientsState = {
  recipients: [],
  loading: false,
  error: null,
  totalCount: 0,
  totalPages: 0,
  selectedRecipients: [],
};

// Async thunks
export const fetchRecipients = createAsyncThunk(
  "recipients/fetchRecipients",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.getRecipients(params);
      // The API returns { success: true, data: { recipients: [], totalCount: 0, ... } }
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch recipients");
    }
  }
);

export const addRecipient = createAsyncThunk(
  "recipients/addRecipient",
  async (recipientData: Partial<BulkEmailRecipient>, { rejectWithValue }) => {
    try {
      const response = await apiService.addRecipient(recipientData);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to add recipient");
    }
  }
);

export const updateRecipient = createAsyncThunk(
  "recipients/updateRecipient",
  async (
    { id, ...recipientData }: { id: string } & Partial<BulkEmailRecipient>,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.updateRecipient(id, recipientData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update recipient");
    }
  }
);

export const deleteRecipient = createAsyncThunk(
  "recipients/deleteRecipient",
  async (id: string, { rejectWithValue }) => {
    try {
      await apiService.deleteRecipient(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete recipient");
    }
  }
);

export const uploadRecipientsCsv = createAsyncThunk(
  "recipients/uploadRecipientsCsv",
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await apiService.uploadRecipientsCsv(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload CSV");
    }
  }
);

const recipientsSlice = createSlice({
  name: "recipients",
  initialState,
  reducers: {
    clearRecipientsError: (state) => {
      state.error = null;
    },
    clearRecipients: (state) => {
      state.recipients = [];
      state.error = null;
      state.selectedRecipients = [];
    },
    toggleRecipientSelection: (state, action: PayloadAction<string>) => {
      const recipientId = action.payload;
      const index = state.selectedRecipients.indexOf(recipientId);
      if (index > -1) {
        state.selectedRecipients.splice(index, 1);
      } else {
        state.selectedRecipients.push(recipientId);
      }
    },
    selectAllRecipients: (state) => {
      state.selectedRecipients = state.recipients.map((r) => r.id);
    },
    clearAllRecipients: (state) => {
      state.selectedRecipients = [];
    },
    setSelectedRecipients: (state, action: PayloadAction<string[]>) => {
      state.selectedRecipients = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch recipients
    builder
      .addCase(fetchRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = action.payload.recipients || [];
        state.totalCount =
          action.payload.totalCount || action.payload.total || 0;
        state.totalPages = action.payload.totalPages || 1;
      })
      .addCase(fetchRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Add recipient
    builder
      .addCase(addRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRecipient.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(addRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update recipient
    builder
      .addCase(updateRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecipient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.recipients.findIndex(
          (recipient) => recipient.id === action.payload.id
        );
        if (index !== -1) {
          state.recipients[index] = action.payload;
        }
      })
      .addCase(updateRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete recipient
    builder
      .addCase(deleteRecipient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecipient.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = state.recipients.filter(
          (recipient) => recipient.id !== action.payload
        );
        state.selectedRecipients = state.selectedRecipients.filter(
          (id) => id !== action.payload
        );
        state.totalCount -= 1;
      })
      .addCase(deleteRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Upload CSV
    builder
      .addCase(uploadRecipientsCsv.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadRecipientsCsv.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh recipients list after successful upload
        // The actual refresh should be handled by the component
      })
      .addCase(uploadRecipientsCsv.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearRecipientsError,
  clearRecipients,
  toggleRecipientSelection,
  selectAllRecipients,
  clearAllRecipients,
  setSelectedRecipients,
} = recipientsSlice.actions;

export default recipientsSlice.reducer;
